import {
    Button,
    Checkbox,
    Collapse,
    FormControlLabel,
    Grid2 as Grid,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { useEffect, useState } from "react"
import { useFetchProjectFullData, useParseAoiFile } from "../../api/ProjectsApi"
import { NexDropzone } from "../../components/Dropzone"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { Label } from "../../components/Typography/Typography"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { AoiShape } from "../../models/aois/AoiShape"
import { useAcquisitionModeStore } from "../../zstore/AcquisitionModeStore"
import { AoiEditMode, useProjectEditStore } from "../../zstore/ProjectEditStore"

export const AOIUpload = () => {
    //Simple Hooks
    const projectNumber = useNexusProjectNumber()

    // State Management Hooks
    const [files, setFiles] = useState<File[]>([])
    const [dropzoneOpen, setDropzoneOpen] = useState(false)
    const selectedAoi = useProjectEditStore((state) => state.selectedAoi)
    const setSelectedAoi = useProjectEditStore((state) => state.setSelectedAoi)
    const modeIds = useAcquisitionModeStore((state) => state.selectedModeIds)
    const aoiMode = useAcquisitionModeStore((state) => state.aoiMode)
    const doPolygonRepairs = useProjectEditStore((state) => state.doPolygonRepairs)
    const togglePolygonRepairs = useProjectEditStore((state) => state.togglePolygonRepairs)
    const aoiEditMode = useProjectEditStore((state) => state.aoiEditMode)
    const setError = useProjectEditStore((state) => state.setError)

    // Tanstack Query Hooks
    const projectResponse = useFetchProjectFullData(projectNumber)
    const parseAoiFileMutation = useParseAoiFile()

    // Use Effects
    useEffect(() => {
        if (aoiEditMode !== AoiEditMode.EDIT && (dropzoneOpen || files.length > 0)) {
            setFiles([])
            setDropzoneOpen(false)
        }
    }, [aoiEditMode])

    useEffect(() => {
        if (parseAoiFileMutation.error?.message != null) {
            setError(parseAoiFileMutation.error?.message)
        }
    }, [parseAoiFileMutation.error?.message])

    // Handler Functions
    const handleFileSelect = (newFiles: File[]) => {
        setFiles(newFiles)
    }

    const handleFileUpload = async () => {
        if (files.length > 0) {
            const sourceFile = files[0]
            const result = await parseAoiFileMutation.mutateAsync({ file: sourceFile, doPolygonRepairs })
            const aoiShape: AoiShape = {
                ...selectedAoi,
                shape: result.aoi ?? selectedAoi?.shape,
                area_m2: result.area_m2 ?? selectedAoi?.area_m2,
                area_contracted_m2: result.area_contracted_m2 ?? selectedAoi?.area_contracted_m2,
                source_file: sourceFile.name,
                mode_ids: modeIds,
                aoi_mode: aoiMode,
            }
            setSelectedAoi(aoiShape)
            handleCancelUpload()
        } else {
            setDropzoneOpen(true)
        }
    }

    const handleCancelUpload = () => {
        setFiles([])
        setDropzoneOpen(false)
    }

    return (
        <Grid>
            <Label>Source File</Label>
            <Grid>
                <TextField
                    disabled
                    label="File Name"
                    variant="outlined"
                    slotProps={{ inputLabel: { shrink: true } }}
                    helperText="Required"
                    value={selectedAoi?.source_file ?? ""}
                    fullWidth={true}
                    sx={{ maxWidth: 500, pb: 2 }}
                />
            </Grid>
            <Collapse in={dropzoneOpen}>
                {dropzoneOpen && ( // reset controlled files on close
                    <>
                        <NexDropzone
                            acceptedFileTypes={[".kml", ".kmz", ".zip"]}
                            filesLimit={1}
                            maxFileSize={5e7}
                            onChange={handleFileSelect}
                        />
                        <Typography sx={{ pb: 2 }} variant="subtitle2">
                            Supported file formats include KML, KMZ, and SHP. Shapefiles must be compressed into a ZIP
                            to ensure that the necessary PRJ and other accompanying files are also included.
                        </Typography>
                    </>
                )}
            </Collapse>
            <Grid sx={{ pb: 2 }}>
                <LoadingButton
                    disabled={projectResponse.isPending || (dropzoneOpen && !files[0])}
                    pending={parseAoiFileMutation.isPending}
                    variant="contained"
                    color="primary"
                    onClick={handleFileUpload}
                >
                    {dropzoneOpen
                        ? "Parse File"
                        : selectedAoi?.source_file
                          ? "Replace Source File"
                          : "Upload Source File"}
                </LoadingButton>
                {dropzoneOpen && (
                    <>
                        <Button
                            disabled={projectResponse.isPending || parseAoiFileMutation.isPending}
                            variant="outlined"
                            color="primary"
                            onClick={handleCancelUpload}
                            sx={{ ml: 2 }}
                        >
                            Cancel
                        </Button>

                        <Tooltip
                            arrow
                            enterDelay={250}
                            title={
                                <span>
                                    <span>
                                        Attempt to repair invalid geometries, causing very minor distortions to the
                                        polygons.
                                    </span>
                                    <br />
                                    <br />
                                    <span>
                                        First, the parser will attempt to buffer the polygons by 0.0. If that fails, the
                                        parse will then attempt to repair the polygons by buffering and unbuffering them
                                        by 0.01.
                                    </span>
                                </span>
                            }
                        >
                            <FormControlLabel
                                disabled={parseAoiFileMutation.isPending}
                                control={
                                    <Checkbox
                                        checked={doPolygonRepairs}
                                        onChange={togglePolygonRepairs}
                                        name="checkedB"
                                        color="primary"
                                    />
                                }
                                label="Repair Polygons"
                                sx={{ ml: 2 }}
                            />
                        </Tooltip>
                    </>
                )}
            </Grid>
        </Grid>
    )
}
